import { Injectable } from '@angular/core';
import { Observable, from, combineLatest, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { 
  Firestore, 
  collection, 
  collectionData, 
  doc, 
  getDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs
} from '@angular/fire/firestore';
import { MetricsService, DashboardMetrics } from './metrics.service';

export interface DashboardStats {
  totalAdmins: number;
  activeAdmins: number;
  totalCards: number;
  activeCards: number;
  totalUsers: number;
  totalBookmarks: number;
  cardMetrics?: {
    totalViews: number;
    totalClicks: number;
    totalShares: number;
    created: number;
    updated: number;
    deleted: number;
  };
  userMetrics?: {
    newUsersToday: number;
    activeUsersToday: number;
    created: number;
    updated: number;
    deleted: number;
  };
  bookmarkMetrics?: {
    totalBookmarksToday: number;
    mostBookmarkedCards: any[];
    created: number;
    updated: number;
    deleted: number;
  };
  lastUpdated: Date;
}

export interface MetricsData {
  totalCount: number;
  activeCount: number;
  todayCount: number;
  timestamp: any;
  details?: any;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(
    private firestore: Firestore,
    private metricsService: MetricsService
  ) {
    console.log('🔥 Dashboard service initialized with Firestore and Metrics service');
  }

  /**
   * Get the count of documents in a Firestore collection
   */
  private getCollectionCount(collectionPath: string, statusFilter?: 'active' | 'inactive'): Observable<number> {
    console.log(`🔢 Counting documents in collection: ${collectionPath}${statusFilter ? ` with status: ${statusFilter}` : ''}`);
    
    try {
      const col = collection(this.firestore, collectionPath);
      const q = statusFilter ? 
        query(col, where('status', '==', statusFilter)) : 
        query(col);

      return from(getDocs(q)).pipe(
        map(snapshot => snapshot.size),
        catchError(error => {
          console.error(`❌ Error counting documents in ${collectionPath}:`, error);
          return of(0);
        })
      );
    } catch (error) {
      console.error(`❌ Error accessing collection ${collectionPath}:`, error);
      return of(0);
    }
  }

  /**
   * Get dashboard statistics by counting documents in Firestore collections
   */
  getDashboardStats(): Observable<DashboardStats> {
    console.log('🔍 Starting to fetch dashboard stats...');
    
    return combineLatest([
      this.getCollectionCount('data/digitvcards/users'), // Total users
      this.getCollectionCount('data/digitvcards/users', 'active'), // Active users
      this.getCollectionCount('data/digitvcards/cards'),
      this.getCollectionCount('data/digitvcards/cards', 'active'), // Active cards
      this.getCollectionCount('data/digitvcards/bookmarks'),
      this.getAdminsMetrics()
    ]).pipe(
      map(([totalUsers, activeUsers, totalCards, activeCards, bookmarksCount, adminMetrics]) => {
        console.log('📊 Collection counts received:', {
          users: { total: totalUsers, active: activeUsers },
          cards: { total: totalCards, active: activeCards },
          bookmarks: bookmarksCount,
          admins: adminMetrics
        });
        
        return {
          totalCards: totalCards,
          activeCards: activeCards,
          totalUsers: totalUsers,
          totalBookmarks: bookmarksCount,
          totalAdmins: adminMetrics.totalCount || 0,
          activeAdmins: adminMetrics.activeCount || 0,
          cardMetrics: {
            totalViews: 0,
            totalClicks: 0,
            totalShares: 0,
            created: 0,
            updated: 0,
            deleted: 0
          },
          userMetrics: {
            newUsersToday: 0,
            activeUsersToday: activeUsers,
            created: 0,
            updated: 0,
            deleted: 0
          },
          bookmarkMetrics: {
            totalBookmarksToday: 0,
            mostBookmarkedCards: [],
            created: 0,
            updated: 0,
            deleted: 0
          },
          lastUpdated: new Date()
        };
      }),
      catchError(error => {
        console.error('❌ Error fetching dashboard stats:', error);
        return of({
          totalAdmins: 0,
          activeAdmins: 0,
          totalCards: 0,
          activeCards: 0,
          totalUsers: 0,
          totalBookmarks: 0,
          cardMetrics: {
            totalViews: 0,
            totalClicks: 0,
            totalShares: 0,
            created: 0,
            updated: 0,
            deleted: 0
          },
          userMetrics: {
            newUsersToday: 0,
            activeUsersToday: 0,
            created: 0,
            updated: 0,
            deleted: 0
          },
          bookmarkMetrics: {
            totalBookmarksToday: 0,
            mostBookmarkedCards: [],
            created: 0,
            updated: 0,
            deleted: 0
          },
          lastUpdated: new Date()
        });
      })
    );
  }

  /**
   * Get admins metrics (direct count from users collection)
   */
  private getAdminsMetrics(): Observable<MetricsData> {
    console.log('👨‍💼 Fetching admins metrics...');
    try {
      const adminsCollection = collection(this.firestore, 'data/digitvcards/admins');
      const adminQuery = query(
        adminsCollection,
        where('role', '==', 'admin'),
        where('status', '==', 'active')
      );
      return from(getDocs(adminQuery)).pipe(
        map((querySnapshot: any) => {
          const admins = querySnapshot.docs.map((doc: any) => doc.data());
          console.log('👨‍💼 Admins found:', admins.length);
          return {
            totalCount: admins.length,
            activeCount: admins.filter((admin: any) => admin['status'] === 'active').length,
            todayCount: 0,
            timestamp: new Date()
          };
        }),
        catchError((error) => {
          console.error('❌ Error fetching admins:', error);
          return of({ totalCount: 0, activeCount: 0, todayCount: 0, timestamp: new Date() });
        })
      );
    } catch (error) {
      console.error('❌ Error in getAdminsMetrics:', error);
      return of({ totalCount: 0, activeCount: 0, todayCount: 0, timestamp: new Date() });
    }
  }
}
